import { REGIONS, type Tab } from "@po/shared";
import { DataTable } from "../components/DataTable";
import { MultiSelect } from "../components/MultiSelect";
import { useDashboard } from "../pages/Dashboard";

/** Sertica's Address Book (SPEC §8.7): every address, not only the user's units'. */
export function AddressesTab() {
  const { filters, setSearch } = useDashboard();
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <MultiSelect
          label="Region"
          options={REGIONS.map((v) => ({ value: v }))}
          value={filters.region ?? []}
          onChange={(v) => setSearch({ region: v.length ? (v as typeof REGIONS[number][]) : undefined })}
        />
        <label className="flex items-center gap-1.5 text-[13px] text-ink2">
          <input
            type="checkbox"
            checked={filters.showDoNotUse !== "1"}
            onChange={(e) => setSearch({ showDoNotUse: e.target.checked ? undefined : "1" })}
          />
          Hide &quot;(DO NOT USE)&quot;
        </label>
        <p className="text-[11.5px] text-muted">
          Every address in Sertica&apos;s address book. The search matches the number, name, place, eProc vendor no., products, contacts and categories; the region is the address&apos;s country&apos;s.
        </p>
      </div>
      <DataTable table="addresses" title="Address book" lens="address" />
    </div>
  );
}

type InfoTab = Extract<Tab, "address-categories" | "address-classifications" | "country-codes" | "delivery-conditions" | "framework-agreements" | "payment-conditions">;

const INFO: Record<InfoTab, { title: string; text: string }> = {
  "address-categories": {
    title: "Address categories",
    text: "Sertica's address categories, with how many address book entries are in each. Each address's categories are read one by one in the background, so the counts grow until the whole book has been read. Click one to see them in the address book.",
  },
  "address-classifications": {
    title: "Address classifications",
    text: "Sertica's address classifications and their action, with how many address book entries have each. Click one to see them in the address book.",
  },
  "country-codes": {
    title: "Country codes",
    text: "Sertica's country codes, with the procurement region and how many address book entries are in each country. Click one to see them in the address book.",
  },
  "delivery-conditions": { title: "Delivery conditions", text: "Sertica's delivery conditions." },
  "framework-agreements": {
    title: "Framework agreements",
    text: "Sertica has no list of framework agreements: these are the address book entries marked as having one, with when it expires. PO issued counts your own, in your units. Click one to open it.",
  },
  "payment-conditions": { title: "Payment conditions", text: "Sertica's payment conditions, with their days and offset." },
};

/** Address Book Info's other lists (SPEC §8.7): Sertica's short lists, and framework agreements from the address book. */
export function AddressInfoTab({ tab }: { tab: Tab }) {
  const { setSearch } = useDashboard();
  if (!(tab in INFO)) return null;
  const t = tab as InfoTab;
  // A category, classification or country opens the address book searched for it.
  const toBook = t === "address-categories" || t === "address-classifications" || t === "country-codes";
  return (
    <div className="space-y-4">
      <p className="text-[11.5px] text-muted">
        {INFO[t].text} Only the search applies here. Changes are made in Sertica.
      </p>
      <DataTable
        key={t}
        table={t}
        title={INFO[t].title}
        lens={t === "framework-agreements" ? "address" : "plain"}
        onRow={toBook ? (r) => r.name && setSearch({ tab: "addresses", q: String(r.name) }) : undefined}
        highlight={(r) => (r.expired ? ["expireDate", "expired"] : [])}
      />
    </div>
  );
}
